/**
 * StaffGroupEditorPage — create, edit or read one staff group (2026-10-24, PP; JAI-3199).
 * Route: staff.group.edit — `new=1` creates, `id` edits, `id` + `view=1` reads.
 *
 * Name, description, hue, status and services are the group record
 * (StaffGroupCreateInput / StaffGroupUpdateInput). Members are NOT on the group:
 * a member joins through the `groups` array on their staff record, so Save writes
 * the group first, then one updateStaff({ groups }) per member whose membership changed.
 * Without staffing.write, or with view=1, nothing on the page writes.
 *
 * Data: bound — reads listStaffGroups, listStaff; writes createStaffGroup, updateStaffGroup,
 * updateStaff (observations in changelog/2026-10-24T1100-staff-group-editor-and-timezone.md).
 */
import { useEffect, useMemo, useState } from "react";
import { alpha, Banner, Btn, Checkbox, ColorPicker, IconBtn, Icon, Input, Pill, T, Textarea } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { LoadState } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useStaff, type StaffGroup, type StaffMember } from "./useStaff";
import { staffName } from "./StaffList";
import { SvcSectionHead, SvcRow, SvcSeg } from "../../lib/svc-editor-primitives";
import { useServices, svcCategoryName, svcCategoryHue, type Service } from "../services/services-data";
import { ServicePicker } from "../services/ServiceExtras";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";
import { useT, formatICU } from "../../lib/i18n";

const DEFAULT_HUE = "#5142C7";
const HEX_RE = /^#[0-9a-f]{6}$/i;

function blankGroup(): StaffGroup {
  return { id: "", name: "", description: "", hue: DEFAULT_HUE, status: "draft", services: [], memberCount: 0, updatedAt: new Date().toISOString() };
}

export default function StaffGroupEditorPage() {
  const t = useT();
  const nav = useNav();
  const store = useStaff();
  const svcStore = useServices();
  const mods = useTenantModules();
  /* Same rule as the Groups tab: with neither booking nor queue composed, services per group do not apply. */
  const eventsOnly = moduleKnownAbsent("booking", mods) && moduleKnownAbsent("queue", mods);

  const isNew = !!nav.params?.new;
  const id = nav.params?.id as string | undefined;
  const mayWrite = can("staffing.write");
  const canWrite = !nav.params?.view && mayWrite;

  const stored = id ? store.groups.find((g) => g.id === id) ?? null : null;
  const [form, setForm] = useState<StaffGroup>(blankGroup);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pickSvc, setPickSvc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<StaffMember[]>([]);

  /* Fill the form once per group, only from a SUCCESSFUL read of both lists; a later cache change does not
     overwrite edits. A failed read (store.error: staff or groups) never hydrates: membership comes from the
     roster, and an empty roster from a failed read would look like "no members" and let Save report success
     over memberships the page never read (CC build report 3). A failure after hydration drops the form so it
     re-fills from the next good read. LOADING is treated the same way (CC build report 4): a workspace switch
     purges the store and sets it loading, so a form filled from the old workspace is dropped there and re-fills
     only from the confirmed workspace's completed read. */
  const key = isNew ? "new" : id ?? "";
  const readFailed = !!store.error;
  useEffect(() => {
    if (readFailed || store.loading) { if (hydratedFor !== null) setHydratedFor(null); return; }
    if (hydratedFor === key) return;
    if (isNew) { setForm(blankGroup()); setSelected(new Set()); setFailed([]); setHydratedFor(key); return; }
    if (!stored) return;
    setForm({ ...stored, hue: stored.hue && HEX_RE.test(stored.hue) ? stored.hue : DEFAULT_HUE });
    setSelected(new Set(store.members.filter((m) => (m.groups ?? []).includes(stored.id)).map((m) => m.id)));
    setFailed([]);
    setHydratedFor(key);
  }, [key, isNew, stored, store.loading, store.members, hydratedFor, readFailed]);

  const set = (patch: Partial<StaffGroup>) => setForm((f) => ({ ...f, ...patch }));
  const services = svcStore.services();
  const groupServices = (form.services ?? []).map((sid) => services.find((s: Service) => s.id === sid)).filter(Boolean) as Service[];

  /* Deactivated people are listed only while they are still in the group, so they can be taken out. */
  const candidates = useMemo(() => store.members
    .filter((m) => m.status !== "inactive" || selected.has(m.id))
    .filter((m) => !query.trim() || `${staffName(m)} ${m.email} ${m.title}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => staffName(a).localeCompare(staffName(b))), [store.members, selected, query]);
  const toggleMember = (mid: string) => setSelected((s) => { const n = new Set(s); if (n.has(mid)) n.delete(mid); else n.add(mid); return n; });

  /* No form until both reads succeeded: the error state (with retry) replaces it, for new and for edit. */
  if (readFailed || hydratedFor !== key) {
    return (
      <Screen rail="staff">
        <div style={{ padding: 32, color: T.soft }}>
          <LoadState
            loading={store.loading}
            error={store.error}
            empty={!readFailed && !store.loading && !isNew && !stored}
            noun={t("staff.groupEditor.groupNoun", "group")}
            onRetry={store.reload}
            loadingLabel={t("common.loading", "Loading…")}
            emptyState={t("staff.groupEditor.notFound", "Staff group not found.")}
            testid="staff-group-editor-group"
          />
        </div>
      </Screen>
    );
  }

  const save = async () => {
    /* Never write over a roster the page could not read. */
    if (saving || !canWrite || readFailed || store.loading || hydratedFor !== key) return;
    if (!form.name.trim()) { toast({ tone: "warn", title: t("staff.groupEditor.addANameFirst", "Add a name first") }); return; }
    /* A workspace switch or sign-out during Save (CC build report 5): after every await, stop silently once the
       generation moved. No toast, no navigation, no further writes; the switch already reset the page. */
    const gen = store.generation();
    const moved = () => store.generation() !== gen;
    setSaving(true);
    setFailed([]);
    let saved: StaffGroup;
    try {
      saved = await store.upsertGroup({ ...form, name: form.name.trim(), description: (form.description ?? "").trim() });
    } catch (e) {
      setSaving(false);
      if (moved()) return;
      console.warn("staff group save failed:", e);
      toast({ tone: "danger", title: t("staff.groupEditor.couldntSaveGroup", "Couldn't save group"), subtitle: t("staff.groupEditor.nothingWasSaved", "Nothing was saved — your edits are still here. Retry in a moment.") });
      return;
    }
    if (moved()) { setSaving(false); return; }
    let missed: StaffMember[] = [];
    try {
      const res = await store.setGroupMembers(saved.id, [...selected]);
      if (res.superseded || moved()) { setSaving(false); return; }
      missed = res.failed;
    } catch (e) {
      if (moved()) { setSaving(false); return; }
      console.warn("group membership save failed:", e);
      missed = store.members.filter((m) => selected.has(m.id) !== (m.groups ?? []).includes(saved.id));
    }
    setSaving(false);
    if (missed.length) {
      /* The group is stored; some memberships are not. Stay on the page, on the stored group, and name them. */
      setFailed(missed);
      toast({ tone: "warn", title: t("staff.groupEditor.groupSavedMembersNot", "Group saved — some members weren't updated"), subtitle: t("staff.groupEditor.saveAgainToRetry", "Save again to retry them.") });
      if (isNew) { setHydratedFor(saved.id); setForm(saved); nav.go("staff.group.edit", { id: saved.id }); }
      return;
    }
    toast({ tone: "success", title: isNew ? t("staff.groupEditor.groupCreated", "Group created") : t("staff.groupEditor.groupSaved", "Group saved") });
    nav.go("staff.groups");
  };

  const title = isNew ? t("staff.list.newGroup", "New group") : (form.name || t("staff.groupEditor.editGroup", "Edit group"));
  const statusPill = form.status === "published"
    ? <Pill tone="success" dot>{t("common.status.published", "Published")}</Pill>
    : <Pill tone="warn" dot>{t("common.status.draft", "Draft")}</Pill>;

  return (
    <Screen rail="staff">
      <PageHeader
        title={title}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{statusPill}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("staff.groups")}><Icon.arrow dir="left" size={11} /> {t("staff.list.staffGroups", "Staff groups")}</span>}
        actions={canWrite ? <>
          <Btn variant="secondary" onClick={() => nav.go("staff.groups")} disabled={saving}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} disabled={saving || readFailed || store.loading} onClick={() => void save()} testid="staff-group-save">{saving ? t("staff.groupEditor.saving", "Saving…") : isNew ? t("staff.staffEditorPage.create", "Create") : t("common.actions.save", "Save")}</Btn>
        </> : mayWrite && !isNew ? (
          <Btn icon={<Icon.edit size={14} />} onClick={() => { setHydratedFor(null); nav.go("staff.group.edit", { id: form.id }); }}>{t("common.actions.edit", "Edit")}</Btn>
        ) : null}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "24px 28px 60px", minWidth: 0 }}>
        <div style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 32 }}>
          {failed.length ? (
            <Banner tone="warning" size="compact">
              {t("staff.groupEditor.membersNotUpdated", "These members weren't updated:")} {failed.map(staffName).join(", ")}
            </Banner>
          ) : null}

          <section>
            <SvcSectionHead icon={<Icon.tags size={18} />} title={t("staff.groupEditor.details", "Details")} />
            <SvcRow label={t("staff.groupEditor.name", "Name")}>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder={t("staff.groupEditor.namePlaceholder", "e.g. Mortgage advisors")} readOnly={!canWrite} testid="staff-group-name" />
            </SvcRow>
            <SvcRow label={t("staff.groupEditor.description", "Description")}>
              <Textarea value={form.description ?? ""} onChange={(e) => set({ description: e.target.value })} rows={3} readOnly={!canWrite} placeholder={t("staff.groupEditor.descriptionPlaceholder", "What this group does")} data-testid="staff-group-description" />
            </SvcRow>
            <SvcRow label={t("staff.groupEditor.colour", "Colour")} hint={t("staff.groupEditor.colourHint", "Marks the group in lists and on staff profiles.")}>
              <div style={{ maxWidth: 320 }}>
                <ColorPicker value={form.hue ?? DEFAULT_HUE} onChange={(hex) => set({ hue: hex })} disabled={!canWrite} aria-label={t("staff.groupEditor.colour", "Colour")} />
              </div>
            </SvcRow>
            <SvcRow label={t("staff.list.status", "Status")} hint={t("staff.groupEditor.statusHint", "Draft groups are kept but not offered when assigning staff.")}>
              <SvcSeg value={form.status} onChange={(v) => set({ status: v as StaffGroup["status"] })} disabled={!canWrite} ariaLabel={t("staff.list.status", "Status")}
                options={[{ id: "draft", label: t("common.status.draft", "Draft") }, { id: "published", label: t("common.status.published", "Published") }]} />
            </SvcRow>
          </section>

          {eventsOnly ? null : (
            <section>
              <SvcSectionHead icon={<Icon.layers size={18} />} title={t("staff.groupEditor.services", "Services")} desc={t("staff.groupEditor.servicesDesc", "Every member of this group can deliver these services.")} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {groupServices.length === 0 ? (
                  <div style={{ padding: "20px 16px", textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.border}`, borderRadius: 10 }}>{t("staff.groupEditor.noServices", "No services yet.")}</div>
                ) : groupServices.map((s) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white }}>
                    <span style={{ width: 30, height: 30, borderRadius: 7, background: alpha(svcCategoryHue(s.category), "1f"), color: svcCategoryHue(s.category), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{s.name}</div>
                      <div style={{ fontSize: 11.5, color: T.soft }}>{svcCategoryName(s.category)}</div>
                    </div>
                    {canWrite ? <IconBtn size={32} onClick={() => set({ services: (form.services ?? []).filter((x) => x !== s.id) })} title={t("common.actions.remove", "Remove")}><Icon.x size={14} /></IconBtn> : null}
                  </div>
                ))}
                {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickSvc(true)}>{t("staff.editorPage.addService", "Add service")}</Btn></div> : null}
              </div>
            </section>
          )}

          <section>
            <SvcSectionHead icon={<Icon.users size={18} />} title={t("staff.list.members", "Members")} desc={t("staff.groupEditor.membersDesc", "Membership is saved on each person's staff record.")} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <Input full={false} size="sm" leading={<Icon.search size={14} />} placeholder={t("staff.groupEditor.searchStaff", "Search staff…")} value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 240, maxWidth: "100%" }} />
              <span style={{ fontSize: 12, color: T.soft }}>{formatICU(t("staff.groupEditor.selectedCount", "{count} selected"), { count: selected.size })}</span>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, maxHeight: 420, overflow: "auto" }}>
              {candidates.length === 0 ? (
                <div style={{ padding: "20px 16px", textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t("staff.list.noStaffMatch", "No staff match") : t("staff.groupEditor.noStaff", "No staff yet.")}</div>
              ) : candidates.map((m, i) => (
                <div key={m.id} style={{ padding: "10px 14px", borderTop: i ? `1px solid ${T.border}` : "none" }}>
                  <Checkbox checked={selected.has(m.id)} disabled={!canWrite} onChange={() => toggleMember(m.id)} label={staffName(m)}
                    supportingText={m.status === "inactive" ? t("common.status.inactive", "Inactive") : (m.title || undefined)} testid="staff-group-member" />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      {pickSvc ? <ServicePicker exclude={form.services ?? []} onPick={(s) => set({ services: [...new Set([...(form.services ?? []), s.id])] })} onClose={() => setPickSvc(false)} /> : null}
    </Screen>
  );
}
